import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { TaskDTO, TaskStatus } from '../client'
import { CancelablePromise } from '../client/core/CancelablePromise'
import { createTaskSocket } from '../hooks/useTaskSocket'

type FetchTaskFunction<T> = (params: { patientId: string }) => CancelablePromise<T>

// T may include null (e.g. the Greenlight gate is disabled → the launch
// endpoint returns null / no task). The runtime guards it: `if (data && …)`.
export const useTaskWorkflow = <T extends TaskDTO | null>(patientId: string, fetchTaskFunction: FetchTaskFunction<T>) => {
    const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [socket, setSocket] = useState<WebSocket | null>(null)
    const { enqueueSnackbar } = useSnackbar()

    const start = async (patientId: string) => {
        const fetchTask = async () => {
            setLoading(true)
            setError(null)
            try {
                const response: CancelablePromise<T> = fetchTaskFunction({ patientId })
                const data = await response
                if (data && 'taskId' in data) {
                    const taskId = data.taskId
                    const taskSocket: WebSocket = createTaskSocket(taskId)
                    setTaskStatus({
                        taskId: taskId,
                        status: 'PENDING',
                        result: {}
                    })
                    setSocket(taskSocket)
                }
            } catch (err: any) {
                if (
                    typeof err === 'object' &&
                    err !== null &&
                    'body' in err &&
                    typeof err.body === 'object' &&
                    err.body !== null &&
                    Array.isArray(err.body.detail) &&
                    err.body.detail.length > 0 &&
                    'msg' in err.body.detail[0]
                ) {
                    const rawMsg = err.body.detail[0].msg
                    const parsedMsg = rawMsg.match(/ValueError\('(.+?)'\)/)?.[1] || rawMsg
                    setError(parsedMsg)
                    enqueueSnackbar(parsedMsg, { variant: 'error' })
                } else {
                    setError('An unexpected error occurred')
                }
            } finally {
                setLoading(false)
            }
        }

        fetchTask()
    }

    useEffect(() => {
        if (!socket) return

        const handleMessage = (event: MessageEvent) => {
            try {
                const message = JSON.parse(event.data)

                if (message.error) {
                    console.error('WebSocket error:', message.error)
                    setError(message.error)
                    enqueueSnackbar(message.error, { variant: 'error' })
                } else if (message.status === 'SUCCESS') {
                    const taskStatus = message as TaskStatus
                    setTaskStatus(taskStatus)
                    enqueueSnackbar(`${taskStatus.result.countFhirResources} FHIR Resources created`, { variant: 'success' })
                    enqueueSnackbar(`${taskStatus.result.countFhirDocuments} FHIR Documents created`, { variant: 'success' })
                    enqueueSnackbar(`${taskStatus.result.countClinicalDocuments} Clinical Documents created`, { variant: 'success' })
                } else if (message.status === 'FAILURE') {
                    const taskStatus = message as TaskStatus
                    setError(taskStatus.result.error)
                    setTaskStatus(taskStatus)
                    enqueueSnackbar(taskStatus.result.error, { variant: 'error' })
                } else {
                    const taskStatus = message as TaskStatus
                    setTaskStatus(taskStatus)
                    console.log('WebSocket message:', taskStatus)
                }
            } catch (e) {
                console.error('Failed to parse WebSocket message:', e)
            }
        }

        const handleClose = () => {
            console.log('WebSocket closed')
        }

        const handleError = () => {
            console.error('WebSocket error')
        }

        socket.addEventListener('message', handleMessage)
        socket.addEventListener('close', handleClose)
        socket.addEventListener('error', handleError)

        return () => {
            socket.removeEventListener('message', handleMessage)
            socket.removeEventListener('close', handleClose)
            socket.removeEventListener('error', handleError)
            socket.close()
        }
    }, [socket, enqueueSnackbar])

    return { taskStatus, loading, error, start }
}
